import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { useEditorStore } from '../stores/editorStore.js';

/** Which content kind this editor is editing — posts and emails share it all. */
export type EditorContentKind = 'post' | 'email';

/**
 * Debounced auto-save for the block editors (posts AND emails).
 *
 * Watches the editor store's dirty flag + blocks and, after a 2s pause,
 * persists the current blocks to the matching API resource. Also exposes the
 * save mutation so the toolbar's Save button and the ⌘S shortcut can trigger
 * an immediate save.
 */
export function useEditorAutosave(kind: EditorContentKind, id: string) {
  const blocks = useEditorStore((s) => s.blocks);
  const componentUsages = useEditorStore((s) => s.componentUsages);
  const isDirty = useEditorStore((s) => s.isDirty);
  const markClean = useEditorStore((s) => s.markClean);
  const qc = useQueryClient();
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout>>();

  const update = kind === 'post' ? api.posts.update : api.emails.update;

  const saveMutation = useMutation({
    mutationFn: () => update(id, { blocks, componentUsages }),
    onSuccess: () => {
      markClean();
      void qc.invalidateQueries({ queryKey: [kind, id] });
    },
  });

  useEffect(() => {
    if (!isDirty) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveMutation.mutate();
    }, 2000); // 2-second debounce
    return () => clearTimeout(autoSaveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, blocks, componentUsages, id]);

  return { saveMutation, isDirty };
}
